/**
 * Archivo principal del plugin Zoho Form Manager
 * Versión refactorizada con módulos
 */

import { FormValidator } from './modules/formValidator.js';
import { SpecialFeatures } from './modules/specialFeatures.js';
import { Notifications } from './modules/notifications.js';
import { PhoneCountryManager } from './modules/phoneCountryManager.js';

// Instancia global del validador
const formValidator = new FormValidator();

/**
 * Valida y envía un formulario
 */
function zf_ValidateAndSubmit(form) {
    return formValidator.validateForm(form, true);
}

/**
 * Configura un formulario con todas sus funcionalidades
 */
function zfm_config_cv_form(form) {
    if (!form) return;
    
    // Configurar validación en tiempo real
    formValidator.setupRealtimeValidation(form);
    
    // Configurar funcionalidades especiales
    PhoneCountryManager.init(form);
    SpecialFeatures.setupFileUpload(form);
    
}

/**
 * Función asíncrona para manejar reCAPTCHA v3 estándar
 */
async function handleRecaptcha(siteKey, action = "SUBMIT_ZOHO_FORM") {
    try {
        // Verificar que grecaptcha esté disponible
        if (typeof grecaptcha === 'undefined') {
            throw new Error('reCAPTCHA no está disponible');
        }

        // Esperar a que reCAPTCHA esté listo con timeout
        await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Timeout al inicializar reCAPTCHA'));
            }, 10000); // 10 segundos de timeout

            try {
                grecaptcha.ready(() => {
                    clearTimeout(timeout);
                    resolve();
                });
            } catch (error) {
                clearTimeout(timeout);
                reject(new Error('Error al inicializar reCAPTCHA: ' + error.message));
            }
        });

        // Ejecutar reCAPTCHA con timeout
        const token = await Promise.race([
            grecaptcha.execute(siteKey, { action }),
            new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Timeout al ejecutar reCAPTCHA')), 15000)
            )
        ]);
        
        if (!token) {
            throw new Error('No se pudo obtener el token de reCAPTCHA');
        }

        // Validar que el token tenga el formato esperado
        if (typeof token !== 'string' || token.length < 10) {
            throw new Error('Token de reCAPTCHA inválido');
        }

        return token;
    } catch (error) {
        console.error('Error en reCAPTCHA:', error);
        throw error;
    }
}

/**
 * Maneja el envío del formulario
 */
async function zfm_on_submit(e) {
    e.preventDefault();
    const form = e.target;

    // Evitar envíos múltiples
    if (form.dataset.zfmSubmitting === '1') return;
    form.dataset.zfmSubmitting = '1';

    // Validar formulario
    if (!formValidator.validateForm(form)) {
        form.dataset.zfmSubmitting = '0';
        return;
    }

    // Deshabilitar botones de submit
    const submitButtons = form.querySelectorAll('[type="submit"]');
    const sendingText = (typeof ZFM_VARS !== 'undefined' && ZFM_VARS.sending_text) ? ZFM_VARS.sending_text : 'Enviando...';
    
    submitButtons.forEach(btn => {
        btn.disabled = true;
        btn.dataset.originalText = btn.value || btn.textContent;
        if (btn.tagName === 'INPUT') {
            btn.value = sendingText;
        } else {
            btn.textContent = sendingText;
        }
    });
    
    try {
        // Enviar con reCAPTCHA si está configurado
        if (ZFM_VARS?.site_key) {
            try {
                // Mostrar indicador de carga para reCAPTCHA
                Notifications.showNotification('Verificando seguridad...', 'info');
                
                const token = await handleRecaptcha(ZFM_VARS.site_key, "SUBMIT_ZOHO_FORM");
                
                // Limpiar notificación de carga
                Notifications.clearNotifications();
                
                // Agregar token al formulario
                let tokenInput = form.querySelector('input[name="g-recaptcha-response"]');
                if (!tokenInput) {
                    tokenInput = document.createElement("input");
                    tokenInput.type = "hidden";
                    tokenInput.name = "g-recaptcha-response";
                    form.appendChild(tokenInput);
                }
                tokenInput.value = token;
                
                // Enviar formulario
                form.submit();
                
            } catch (recaptchaError) {
                console.error('Error de reCAPTCHA:', recaptchaError);
                
                // Limpiar notificación de carga
                Notifications.clearNotifications();
                
                const i18n = (typeof ZFM_VARS !== 'undefined' && ZFM_VARS.i18n) ? ZFM_VARS.i18n : {};
                
                // Mostrar mensaje de error específico según el tipo de error
                let errorMessage = i18n.recaptcha_error || 'Error al validar reCAPTCHA. Inténtalo de nuevo.';
                
                if (recaptchaError.message.includes('no está disponible')) {
                    errorMessage = i18n.recaptcha_unavailable || 'El servicio de verificación no está disponible. Inténtalo más tarde.';
                } else if (recaptchaError.message.includes('inicializar') || recaptchaError.message.includes('Timeout al inicializar')) {
                    errorMessage = i18n.recaptcha_load_error || 'Error al cargar la verificación de seguridad. Recarga la página e inténtalo de nuevo.';
                } else if (recaptchaError.message.includes('token') || recaptchaError.message.includes('Timeout al ejecutar')) {
                    errorMessage = i18n.recaptcha_token_error || 'No se pudo completar la verificación. Inténtalo de nuevo.';
                } else if (recaptchaError.message.includes('Enterprise')) {
                    errorMessage = i18n.recaptcha_enterprise_error || 'Servicio de verificación no compatible. Contacta al administrador.';
                }
                
                Notifications.showNotification(errorMessage, 'error');
                throw recaptchaError; // Re-lanzar para el catch general
            }
        } else {
            // Enviar sin reCAPTCHA si no está configurado
            form.submit();
        }
        
    } catch (error) {
        // Manejo general de errores
        console.error('Error en el envío del formulario:', error);
        
        // Reactivar formulario
        form.dataset.zfmSubmitting = '0';
        submitButtons.forEach(btn => {
            btn.disabled = false;
            if (btn.dataset.originalText) {
                if (btn.tagName === 'INPUT') {
                    btn.value = btn.dataset.originalText;
                } else {
                    btn.textContent = btn.dataset.originalText;
                }
            }
        });
        
        // Si no se mostró un mensaje específico, mostrar uno genérico
        if (!error.message.includes('reCAPTCHA') && !error.message.includes('verificación')) {
            const i18n = (typeof ZFM_VARS !== 'undefined' && ZFM_VARS.i18n) ? ZFM_VARS.i18n : {};
            Notifications.showNotification(i18n.form_submit_error || 'Error al enviar el formulario. Inténtalo de nuevo.', 'error');
        }
    }
}

/**
 * Función de navegación entre campos de teléfono (compatibilidad)
 */
function zf_FocusNext(elem, event) {
    SpecialFeatures.setupPhoneNavigation(elem, event);
}

/**
 * Función llamada desde el script inline del formulario para 
 * establecer las expresiones regulares basadas en el formato de fecha
 */
function zf_SetDateAndMonthRegexBasedOnDateFormate(dateFormat) {

    var dateAndMonthRegexFormateArray = new Array();
    var dateFormatRegExp;
    var monthYearFormatRegExp;
    if (dateFormat === "dd-MMM-yyyy") {

        dateFormatRegExp = "^(([0][1-9])|([1-2][0-9])|([3][0-1]))[-](Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[-](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[-](?:(?:19|20)[0-9]{2})$";// No I18N

    } else if (dateFormat === "dd-MMMM-yyyy") {// No I18N
        dateFormatRegExp = "^(([0][1-9])|([1-2][0-9])|([3][0-1]))[-](January|February|March|April|May|June|July|August|September|October|November|December|JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)[-](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^(January|February|March|April|May|June|July|August|September|October|November|December|JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)[-](?:(?:19|20)[0-9]{2})$";// No I18N

    } else if (dateFormat === "MMMM-dd-yyyy") {// No I18N
        dateFormatRegExp = "^(January|February|March|April|May|June|July|August|September|October|November|December|JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)[-](([0][1-9])|([1-2][0-9])|([3][0-1]))[-](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^(January|February|March|April|May|June|July|August|September|October|November|December|JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)[-](?:(?:19|20)[0-9]{2})$";// No I18N

    } else if (dateFormat === "dd/MM/yyyy") {// No I18N
        dateFormatRegExp = "^(([0][1-9])|([1-2][0-9])|([3][0-1]))[\/]([0][1-9]|1[012])[\/](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^([0][1-9]|1[012])[\/](?:(?:19|20)[0-9]{2})$";

    } else if (dateFormat === "dd-MM-yyyy") {// No I18N
        dateFormatRegExp = "^(([0][1-9])|([1-2][0-9])|([3][0-1]))[-]([0][1-9]|1[012])[-](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^([0][1-9]|1[012])[-](?:(?:19|20)[0-9]{2})$";

    } else if (dateFormat === "MM/dd/yyyy") {// No I18N
        dateFormatRegExp = "^([0][1-9]|1[012])[\/](([0][1-9])|([1-2][0-9])|([3][0-1]))[\/](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^([0][1-9]|1[012])[\/](?:(?:19|20)[0-9]{2})$";

    } else if (dateFormat === "yyyy-MM-dd") {// No I18N
        dateFormatRegExp = "^(?:(?:19|20)[0-9]{2})[-]([0][1-9]|1[012])[-](([0][1-9])|([1-2][0-9])|([3][0-1]))$";
        monthYearFormatRegExp = "^(?:(?:19|20)[0-9]{2})[-]([0][1-9]|1[012])$";

    } else if (dateFormat === "yyyy/MM/dd") {// No I18N
        dateFormatRegExp = "^(?:(?:19|20)[0-9]{2})[\/]([0][1-9]|1[012])[\/](([0][1-9])|([1-2][0-9])|([3][0-1]))$";
        monthYearFormatRegExp = "^(?:(?:19|20)[0-9]{2})[\/]([0][1-9]|1[012])$";

    } else if (dateFormat === "dd.MM.yyyy") {// No I18N
        dateFormatRegExp = "^(([0][1-9])|([1-2][0-9])|([3][0-1]))[.]([0][1-9]|1[012])[.](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^([0][1-9]|1[012])[.](?:(?:19|20)[0-9]{2})$";

    } else if (dateFormat === "MM-dd-yyyy") {// No I18N
        dateFormatRegExp = "^([0][1-9]|1[012])[-](([0][1-9])|([1-2][0-9])|([3][0-1]))[-](?:(?:19|20)[0-9]{2})$";
        monthYearFormatRegExp = "^([0][1-9]|1[012])[-](?:(?:19|20)[0-9]{2})$";
    }
    dateAndMonthRegexFormateArray.push(dateFormatRegExp);
    dateAndMonthRegexFormateArray.push(monthYearFormatRegExp);

    return dateAndMonthRegexFormateArray;


}

/**
 * Inicialización cuando el DOM está listo
 */
document.addEventListener('DOMContentLoaded', () => {
    // Configurar envío de formularios
    document.body.addEventListener('submit', (e) => {
        if (e.target.matches('.zfm-form')) {
            zfm_on_submit(e);
        }
    });

    // Configurar formularios existentes
    const forms = document.querySelectorAll('.zfm-form');
    forms.forEach(form => {
        zfm_config_cv_form(form);
    });

    // Observer para formularios añadidos dinámicamente
    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType === 1) {
                    if (node.matches('form.zfm-form')) {
                        zfm_config_cv_form(node);
                    } else if (node.querySelector('form.zfm-form')) {
                        const form = node.querySelector('form.zfm-form');
                        zfm_config_cv_form(form);
                    }
                }
            }
        }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });

    // Configurar notificaciones
    Notifications.setupNotifications();
});

// Exponer funciones globales para compatibilidad
window.zf_ValidateAndSubmit = zf_ValidateAndSubmit;
window.zf_FocusNext = zf_FocusNext;
window.zfm_config_cv_form = zfm_config_cv_form;
window.zf_SetDateAndMonthRegexBasedOnDateFormate = zf_SetDateAndMonthRegexBasedOnDateFormate;
